import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import notificationService from '../services/notificationService';
import { useAuth } from './AuthContext';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'info' | 'warning' | 'error';
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  actionText?: string;
  category?: 'system' | 'user' | 'security' | 'update' | 'approval';
  priority?: 'low' | 'medium' | 'high' | 'critical';
  entityType?: string;
  entityId?: string;
  actionData?: Record<string, any>;
  sender?: {
    id: string;
    name: string;
    email: string;
  };
}

interface StatutoryStageNotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAsUnread: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  refetchNotifications: () => Promise<void>;
}

const StatutoryStageNotificationContext = createContext<StatutoryStageNotificationContextType | undefined>(undefined);

export const STATUTORY_STAGE_ID = 'a4d58cfb-84f3-4e6d-93eb-46b85dad826c';
export const STATUTORY_STAGE_KEY = 'statutory';

export const StatutoryStageNotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getNotificationScope = useCallback(() => {
    const selectedPartyId = localStorage.getItem('selectedPartyId');
    const selectedStageId = localStorage.getItem('selectedStageId');

    return {
      partyId: selectedPartyId || null,
      stageId: selectedStageId || STATUTORY_STAGE_ID,
    };
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter(n => !n.read).length,
    [notifications]
  );

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true } 
            : notification
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Failed to mark notification as read');
    }
  }, []);

  const markAsUnread = useCallback(async (notificationId: string) => {
    try {
      await notificationService.markAsUnread(notificationId);
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: false } 
            : notification
        )
      );
    } catch (err) {
      console.error('Error marking notification as unread:', err);
      setError('Failed to mark notification as unread');
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const scope = getNotificationScope();
    try {
      const params: {
        stageId?: string;
        appName?: string;
        partyId?: string;
        userId?: string;
      } = {
        stageId: scope.stageId,
        appName: STATUTORY_STAGE_KEY,
      };

      if (scope.partyId) {
        params.partyId = scope.partyId;
      }

      if (user?.id) {
        params.userId = user.id;
      }
      
      const result = await notificationService.markAllAsRead(params);
      if (result.success) {
        setNotifications(prev => 
          prev.map(notification => ({ ...notification, read: true }))
        );
      }
    } catch (err) {
      setError('Failed to mark all notifications as read');
    }
  }, [getNotificationScope, user?.id]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications(prev => 
        prev.filter(notification => notification.id !== notificationId)
      );
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError('Failed to delete notification');
    }
  }, []);

  const clearAllNotifications = useCallback(async () => {
    const scope = getNotificationScope();
    try {
      const params: {
        stageId?: string;
        appName?: string;
        partyId?: string;
        userId?: string;
      } = {
        stageId: scope.stageId,
        appName: STATUTORY_STAGE_KEY,
      };

      if (scope.partyId) {
        params.partyId = scope.partyId;
      }

      if (user?.id) {
        params.userId = user.id;
      }
      
      const result = await notificationService.clearAllNotifications(params);
      if (result.success) {
        setNotifications([]);
      }
    } catch (err) {
      setError('Failed to clear notifications');
    }
  }, [getNotificationScope, user?.id]);

  const refetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setNotifications([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const scope = getNotificationScope();
      const selectedPartyId = scope.partyId;
      const selectedStageId = scope.stageId;

      const filters: Record<string, string> = {
        stageId: selectedStageId,
        appName: STATUTORY_STAGE_KEY,
      };

      if (user?.id) {
        filters.userId = user.id;
      }

      if (selectedPartyId) {
        filters.partyId = selectedPartyId;
      }

      if (selectedStageId && selectedStageId !== STATUTORY_STAGE_ID) {
        console.warn('Statutory stage notification context being used outside of statutory stage');
      }
            
      const response = await notificationService.getNotifications(filters);
      if (response.success) {
        const transformedNotifications: Notification[] = response.data.map((item: any) => ({
          id: item.id,
          title: item.title,
          message: item.message,
          type: item.type,
          timestamp: new Date(item.timestamp),
          read: item.read,
          actionUrl: item.actionUrl,
          actionText: item.actionText,
          category: item.category,
          priority: item.priority,
          entityType: item.entityType,
          entityId: item.entityId,
          actionData: item.actionData,
          sender: item.sender
        }));
        setNotifications(transformedNotifications);
      }
    } catch (err) {
      setError('Failed to fetch notifications');
      console.error('Error fetching notifications:', err);      
    } finally {
      setIsLoading(false);
    }
  }, [getNotificationScope, isAuthenticated, user?.id]);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      refetchNotifications();
    } else {
      setNotifications([]);
    }
  }, [isAuthenticated, user?.id, refetchNotifications]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if ((e.key === 'selectedPartyId' || e.key === 'selectedStageId') && isAuthenticated) {
        refetchNotifications();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isAuthenticated, refetchNotifications]);

  const contextValue = useMemo<StatutoryStageNotificationContextType>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      error,
      markAsRead,
      markAsUnread,
      markAllAsRead,
      deleteNotification,
      clearAllNotifications,
      refetchNotifications,
    }),
    [
      notifications,
      unreadCount,
      isLoading,
      error,
      markAsRead,
      markAsUnread,
      markAllAsRead,
      deleteNotification,
      clearAllNotifications,
      refetchNotifications,
    ]
  );

  return (
    <StatutoryStageNotificationContext.Provider value={contextValue}>
      {children}
    </StatutoryStageNotificationContext.Provider>
  );
};

export const useStatutoryStageNotifications = (): StatutoryStageNotificationContextType => {
  const context = useContext(StatutoryStageNotificationContext);

  if (context === undefined) {
    throw new Error('useStatutoryStageNotifications must be used within a StatutoryStageNotificationProvider');
  }

  return context;
};
